import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/context/ProtectedRoute";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import ClientDashboard from "@/pages/ClientDashboard";
import Documents from "@/pages/Documents";
import Filings from "@/pages/Filings";
import IFTACalculator from "@/pages/IFTACalculator";
import Safety from "@/pages/Safety";
import CSAScore from "@/pages/CSAScore";
import Drivers from "@/pages/Drivers";
import Vehicles from "@/pages/Vehicles";
import Notifications from "@/pages/Notifications";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminSafety from "@/pages/admin/AdminSafety";
import AdminCarrierLookup from "@/pages/admin/AdminCarrierLookup";
import AdminCSA from "@/pages/admin/AdminCSA";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSettings from "@/pages/admin/AdminSettings";
import SuperAdminDashboard from "@/pages/superadmin/SuperAdminDashboard";
import "@/App.css";

function HomeRedirect() {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-slate-900">
        <div className="font-mono text-xs tracking-[0.3em] text-slate-500 animate-pulse">[ LOADING ]</div>
      </div>
    );
  }
  if (user) {
    if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <Landing />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {/* Client */}
              <Route path="/dashboard" element={<ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute role="client"><Documents /></ProtectedRoute>} />
              <Route path="/filings" element={<ProtectedRoute role="client"><Filings /></ProtectedRoute>} />
              <Route path="/ifta" element={<ProtectedRoute role="client"><IFTACalculator /></ProtectedRoute>} />
              <Route path="/safety" element={<ProtectedRoute role="client"><Safety /></ProtectedRoute>} />
              <Route path="/csa-score" element={<ProtectedRoute role="client"><CSAScore /></ProtectedRoute>} />
              <Route path="/drivers" element={<ProtectedRoute role="client"><Drivers /></ProtectedRoute>} />
              <Route path="/vehicles" element={<ProtectedRoute role="client"><Vehicles /></ProtectedRoute>} />
              <Route path="/notifications" element={<Notifications />} />

              {/* Admin (compliance head) — NO money pages */}
              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/clients" element={<ProtectedRoute role="admin"><AdminClients /></ProtectedRoute>} />
              <Route path="/admin/clients/:id" element={<ProtectedRoute role="admin"><AdminClientDetail /></ProtectedRoute>} />
              <Route path="/admin/safety" element={<ProtectedRoute role="admin"><AdminSafety /></ProtectedRoute>} />
              <Route path="/admin/carrier-lookup" element={<ProtectedRoute role="admin"><AdminCarrierLookup /></ProtectedRoute>} />
              <Route path="/admin/csa" element={<ProtectedRoute role="admin"><AdminCSA /></ProtectedRoute>} />
              <Route path="/admin/leads" element={<ProtectedRoute role="admin"><AdminLeads /></ProtectedRoute>} />
              <Route path="/admin/emails" element={<ProtectedRoute role="admin"><AdminEmailLog /></ProtectedRoute>} />
              <Route path="/admin/support" element={<ProtectedRoute role="admin"><AdminSupport /></ProtectedRoute>} />

              {/* Super Admin (CEO) */}
              <Route path="/super-admin" element={<ProtectedRoute role="super_admin"><SuperAdminDashboard /></ProtectedRoute>} />
              <Route path="/super-admin/settings" element={<ProtectedRoute role="super_admin"><AdminSettings /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" theme="dark" />
      </AuthProvider>
    </div>
  );
}
